import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd';
import { MailOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import salesPerformanceReportService from '../../services/salesPerformanceReportService';

export default function SalesPerformanceReportPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canSend = isSuperAdmin || hasPermission(PERMISSIONS.SALES_PERFORMANCE_REPORT_SEND);
  const canManageRecipients = isSuperAdmin || hasPermission(PERMISSIONS.REPORT_EMAIL_RECIPIENTS_MANAGE);

  const [reportDate, setReportDate] = useState(dayjs());
  const [htmlPreview, setHtmlPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [recipients, setRecipients] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const loadRecipients = useCallback(async () => {
    setRecipientsLoading(true);
    try {
      const list = await salesPerformanceReportService.listRecipients();
      setRecipients(list);
    } catch (e) {
      message.error(e?.message || 'Failed to load recipients');
      setRecipients([]);
    } finally {
      setRecipientsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecipients();
  }, [loadRecipients]);

  const filteredRecipients = useMemo(() => {
    const q = recipientSearch.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter(
      (r) =>
        String(r.email || '').toLowerCase().includes(q)
        || String(r.name || '').toLowerCase().includes(q),
    );
  }, [recipients, recipientSearch]);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const dateStr = reportDate.format('YYYY-MM-DD');
      const previewResult = await salesPerformanceReportService.previewHtml(dateStr);
      setHtmlPreview(previewResult?.html ?? '');
      setPreviewOpen(true);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const dateStr = reportDate.format('YYYY-MM-DD');
      const result = await salesPerformanceReportService.sendReport({
        reportDate: dateStr,
        emailTo: 'CRON',
      });
      const email = result?.email;
      if (email?.skipped) {
        message.warning('No active recipients configured');
        return;
      }
      message.success(`Report sent to ${email?.sent ?? 0} recipient(s)`);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to send report');
    } finally {
      setSending(false);
    }
  };

  const openCreateRecipient = () => {
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setDrawerOpen(true);
  };

  const onCreateRecipient = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const res = await salesPerformanceReportService.createRecipient({
        email: values.email.trim(),
        name: values.name?.trim() || null,
        is_active: values.is_active,
      });
      if (res?.success === false) {
        message.error(res?.message || 'Create failed');
        return;
      }
      message.success('Recipient added');
      setDrawerOpen(false);
      loadRecipients();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleRecipient = async (record) => {
    try {
      const res = await salesPerformanceReportService.updateRecipient(record.id, {
        is_active: !record.is_active,
      });
      if (res?.success === false) {
        message.error(res?.message || 'Update failed');
        return;
      }
      loadRecipients();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Update failed');
    }
  };

  const deleteRecipient = async (record) => {
    try {
      const res = await salesPerformanceReportService.removeRecipient(record.id);
      if (res?.success === false) {
        message.error(res?.message || 'Delete failed');
        return;
      }
      message.success('Recipient removed');
      loadRecipients();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Delete failed');
    }
  };

  const recipientColumns = [
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (active ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        canManageRecipients ? (
          <Space>
            <Switch checked={record.is_active} onChange={() => toggleRecipient(record)} size="small" />
            <Popconfirm title="Remove this recipient?" onConfirm={() => deleteRecipient(record)}>
              <Button type="link" danger size="small">Delete</Button>
            </Popconfirm>
          </Space>
        ) : null
      ),
    },
  ];

  return (
    <DashboardPageWrapper>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Performance Report</h1>
          <p className="text-sm text-gray-500 mt-1">
            MTD + FYTD leadership report — empanelment and revenue
          </p>
        </div>
        <Space wrap className="sm:justify-end">
          <DatePicker value={reportDate} onChange={(v) => v && setReportDate(v)} allowClear={false} />
          <Button icon={<ReloadOutlined />} onClick={loadPreview} loading={loading}>
            Preview
          </Button>
          {canSend && (
            <Button type="primary" icon={<MailOutlined />} onClick={handleSend} loading={sending}>
              Send Now
            </Button>
          )}
        </Space>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gray-50/50">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 m-0">Email Recipients</h2>
            <p className="text-sm text-gray-500 mt-1 mb-0">
              Recipients for the daily sales performance report
            </p>
          </div>
          {canManageRecipients && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateRecipient}>
              Add Recipient
            </Button>
          )}
        </div>
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3">
          <Input.Search
            placeholder="Search by email or name..."
            allowClear
            value={recipientSearch}
            onChange={(e) => setRecipientSearch(e.target.value)}
            className="w-full sm:max-w-xs"
          />
          {recipientSearch.trim() && (
            <span className="text-sm text-gray-500">
              Showing {filteredRecipients.length} of {recipients.length}
            </span>
          )}
        </div>
        <Table
          rowKey="id"
          columns={recipientColumns}
          dataSource={filteredRecipients}
          loading={recipientsLoading}
          pagination={false}
          size="middle"
        />
      </div>

      <Modal
        title={`Sales Performance Report — ${reportDate.format('YYYY-MM-DD')}`}
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={960}
        destroyOnClose
      >
        {htmlPreview ? (
          <iframe
            title="Sales Performance Report email preview"
            srcDoc={htmlPreview}
            style={{
              width: '100%',
              minHeight: 640,
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              background: '#f4f4f4',
            }}
          />
        ) : null}
      </Modal>

      <Drawer
        title="Add Report Recipient"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={400}
        extra={(
          <Button type="primary" onClick={onCreateRecipient} loading={submitting}>
            Save
          </Button>
        )}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input placeholder="recipient@example.com" />
          </Form.Item>
          <Form.Item name="name" label="Name">
            <Input placeholder="Optional display name" />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </DashboardPageWrapper>
  );
}
